import type { BaseQLSelectionSet } from '@adornis/baseql/utils/queryGeneration.js';
import { RXController } from '@adornis/chemistry/controllers/RXController.js';
import { css } from '@adornis/chemistry/directives/css.js';
import { FormController } from '@adornis/forms/x-form-controller.js';
import type { ChromiumButton } from '@chancenwerk/client/design/components/cr-button.js';
import { ChromiumDialog } from '@chancenwerk/client/design/components/cr-dialog.js';
import { ChromiumSnackbar } from '@chancenwerk/client/design/components/cr-snackbar.js';
import { Course } from '@chancenwerk/shared/courses/Course.js';
import { StudentAPI } from '@chancenwerk/shared/master-data-management/users/api/StudentAPI.js';
import {
  EntryInformation,
  SchoolInformation,
  Student,
} from '@chancenwerk/shared/master-data-management/users/entities/Student.js';
import { Role } from '@chancenwerk/shared/semantics/Role.js';
import { SemanticRelationship } from '@chancenwerk/shared/semantics/SemanticRelationship.js';
import { StudentParticipationRelationship } from '@chancenwerk/shared/semantics/StudentParticipationRelationship.js';
import { Registermethode, StudentType } from '@chancenwerk/enums/enums.js';
import { html, nothing } from 'lit';
import { customElement, property, state } from 'lit/decorators.js';
import { createRef, type Ref, ref } from 'lit/directives/ref.js';
import { repeat } from 'lit/directives/repeat.js';
import { switchMap } from 'rxjs';

const studentSelectionSet = {
  _id: 1,
  _class: 1,
  vorname: 1,
  nachname: 1,
  username: 1,
  studentID: 1,
  schuelertyp: 1,
  schoolInformation: SchoolInformation.allFields,
  joinInformation: EntryInformation.allFields,
} as BaseQLSelectionSet<Student>;

@customElement('board-dialog-add-guest-student-course')
export class BoardDialogAddGuestStudentCourse extends ChromiumDialog<string> {
  @property({ attribute: false }) kpfID: string = '';
  @property({ attribute: false }) courses: Course[] = [];

  @state() _studentToCreate = new RXController<Student>(
    this,
    new Student({
      guestAccount: true,
      schuelertyp: StudentType.US,
      schoolInformation: new SchoolInformation({ jahrgangsstufe: 0, zug: '' }),
      joinInformation: new EntryInformation({ registermethode: Registermethode.GUEST }),
    }),
  );

  _finishing = false;
  @state() _form = new FormController(this, this._studentToCreate.observable);
  @state() _similar = new RXController(
    this,
    this._form.document?.whenChanging.pipe(
      switchMap(async () => {
        if (
          !this._form.document?.vorname ||
          this._form.document.vorname.length === 0 ||
          !this._form.document.nachname ||
          this._form.document.nachname.length === 0
        ) {
          return [] as Student[];
        }
        const res = await StudentAPI.findSimilarOSAcquisition(this._form.document)(studentSelectionSet);
        return res;
      }),
    ),
  );

  _submitButtonRef: Ref<ChromiumButton> = createRef();

  static override get element_name() {
    return 'board-dialog-add-guest-student-course';
  }

  override heading = 'Gast-Schüler:in hinzufügen';

  private _showSimilar() {
    if ((this._similar.value?.length ?? 0) === 0 || this._finishing) {
      return false;
    }
    return true;
  }

  private _similarContent() {
    if (!this._showSimilar()) {
      return nothing;
    }

    return html`<cr-flex space="md">
      <cr-text bold>Ähnliche OS-Interessent:in gefunden:</cr-text>
      ${repeat(
        this._similar.value ?? [],
        item => item._id,
        item => {
          return html`<cr-flex
            space="sm"
            horizontal
            space-between
            crossaxis-center
            ${css({ borderBottom: '1px solid rgba(0,0,0,0.1)', paddingBottom: this.spacing.sm })}
          >
            <cr-flex space="sm">
              <cr-text bold>${item.vorname} ${item.nachname}</cr-text>
              <cr-text>OS-Interessent:in</cr-text>
            </cr-flex>
            <cr-button
              @click=${async () => {
                await this._save(item);
              }}
            >
              Hinzufügen
            </cr-button>
          </cr-flex>`;
        },
      )}
    </cr-flex>`;
  }

  private _inputContent() {
    if (this._finishing) {
      return html`<cr-text>Gast-Nutzer wird erstellt...</cr-text>`;
    }

    return html`<cr-flex space="md">
      <cr-input placeholder="Vorname" ${this._form.field('vorname')}> </cr-input>
      <cr-input placeholder="Nachname" ${this._form.field('nachname')}> </cr-input>
      <cr-dropdown-selection
        placeholder="Jahrgangsstufe"
        ${this._form.field('schoolInformation', 'jahrgangsstufe')}
        .selectables=${[1, 2, 3, 4, 5, 6, 7, 8, 9, 10, 11, 12, 13]}
      >
      </cr-dropdown-selection>
      <cr-input placeholder="Zug" ${this._form.field('schoolInformation', 'zug')}> </cr-input>
      <cr-dropdown-selection
        clearable
        ${this._form.field('schuelertyp')}
        placeholder="Schüler:innentyp"
        .selectables=${Object.values(StudentType)}
      ></cr-dropdown-selection>
    </cr-flex> `;
  }

  override content() {
    return html`<cr-flex space="md" ${css({ width: this._showSimilar() ? '800px' : '400px' })}>
      <cr-grid columns="${this._showSimilar() ? '1fr 1fr' : '1fr'}" space="md">
        ${this._inputContent()} ${this._similarContent()}
      </cr-grid>
      <cr-button ${css({ maxWidth: '200px' })} @click=${async () => this._save()} ${ref(this._submitButtonRef)}>
        Erstellen
      </cr-button>
    </cr-flex>`;
  }

  private async _save(student?: Student) {
    if (this.courses.length === 0) {
      void ChromiumSnackbar.show('Kein Kurs gewählt!');
      return;
    }

    if (this._submitButtonRef.value) {
      this._submitButtonRef.value.loading = true;
    }

    try {
      this._finishing = true;

      const needCreation = !student;
      student ??= await Student.createStudent(this._studentToCreate.value)(studentSelectionSet);

      if (!student) {
        throw new Error('error while setting student');
      }

      if (needCreation) {
        // * Set KPF id if present
        if (this.kpfID) {
          const rel = new StudentParticipationRelationship({
            subjectID: student._id,
            predicateID: Role.STUDENT,
            objectID: this.kpfID,
          });
          await SemanticRelationship.saveSemanticRelationshipsAndUpdateRoles([rel])();
        }

        await Student.createDebitorennummer(student)();
      } else {
        await StudentAPI.removeOSAcquisitionTag(student._id)();
      }

      this._studentToCreate.next(student);
      for (const c of this.courses) {
        Course.assignUserToCourse(student, c, {
          arrayToAssign: this._studentToCreate.value.schuelertyp === StudentType.OS ? 'OS' : 'US',
        });
        await c.save();
      }

      await this.close(student._id);
    } catch (err: any) {
      void ChromiumSnackbar.show(err.message);
    } finally {
      if (this._submitButtonRef.value) {
        this._submitButtonRef.value.loading = false;
      }
    }

    this._finishing = false;
  }
}
